import React, { useState, useEffect, useRef, useMemo } from 'react';
import GameApp from './GameApp';
import socket from './lib/socket';
import { registerUser, loginUser } from './lib/api';
import { DS } from './theme';
import { supabase } from './lib/supabase';

export default function App() {
  const [authMode, setAuthMode]     = useState('login');
  const [email, setEmail]           = useState('');
  const [username, setUsername]     = useState('');
  const [password, setPassword]     = useState('');
  const [loginUsername, setLoginUsername] = useState('');
  const [user, setUser]             = useState(null);
  const [sessionLoading, setSessionLoading] = useState(true);
  const [message, setMessage]       = useState('');
  const [authLoading, setAuthLoading] = useState(false);
  const [matchState, setMatchState]       = useState(null); // null | "searching" | "in_game"
  const [gameId, setGameId]               = useState(null);
  const [playerColor, setPlayerColor]     = useState(null);
  const [opponent, setOpponent]           = useState(null);
  const [lastEloChange, setLastEloChange] = useState(null);
  const [reconnectMoves, setReconnectMoves]       = useState(null);
  const [opponentDisconnected, setOpponentDisconnected] = useState(false);
  const [hasResumeGame, setHasResumeGame] = useState(() => {
    try {
      const stored = localStorage.getItem('checkmate_active_game');
      if (!stored) return false;
      const { timestamp } = JSON.parse(stored);
      return Date.now() - timestamp < 90000; // generous window; server is authoritative at 45s
    } catch { return false; }
  });
  const [resumeExpired, setResumeExpired] = useState(false);
  const playerColorRef = useRef(null);

  const ACTIVE_GAME_KEY = 'checkmate_active_game';
  const ABANDON_GRACE   = 45000; // ms — must match server

  // ── Restore session ─────────────────────────────────────────────────────────
  useEffect(() => {
    const saved = localStorage.getItem('checkmateUser');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setUser(parsed);
        // Sync the client-side Supabase instance so getSession() works everywhere
        if (parsed.token && parsed.refreshToken) {
          supabase.auth.setSession({ access_token: parsed.token, refresh_token: parsed.refreshToken })
            .catch(() => {});
        }
      } catch {}
    }
    setSessionLoading(false);
  }, []);

  // ── Auto-refresh token every 50 min (Supabase tokens expire after 1 hour) ──
  useEffect(() => {
    if (!user?.refreshToken) return;
    const id = setInterval(async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken: user.refreshToken }),
        });
        const data = await res.json();
        if (data.token) {
          const updated = { ...user, token: data.token, refreshToken: data.refreshToken };
          localStorage.setItem('checkmateUser', JSON.stringify(updated));
          setUser(updated);
          supabase.auth.setSession({ access_token: data.token, refresh_token: data.refreshToken }).catch(() => {});
        } else {
          // Refresh failed — force re-login
          localStorage.removeItem('checkmateUser');
          setUser(null);
        }
      } catch {}
    }, 50 * 60 * 1000);
    return () => clearInterval(id);
  }, [user]);

  // ── Socket Connection Management ───────────────────────────────────────────
  useEffect(() => {
    if (user?.token) {
      socket.auth = { token: user.token };
      socket.connect();
    } else {
      socket.disconnect();
    }
  }, [user]);

  // ── Auto-reconnect on socket connect (handles page refresh mid-game) ────────
  useEffect(() => {
    if (!user) return;

    function tryReconnect() {
      const stored = localStorage.getItem(ACTIVE_GAME_KEY);
      if (!stored) return;
      try {
        const { gameId: storedId } = JSON.parse(stored);
        socket.emit('reconnect_game', { userId: user.userId, gameId: storedId });
      } catch { localStorage.removeItem(ACTIVE_GAME_KEY); }
    }

    // Fire immediately if the socket is already connected (SPA navigation / fast reconnect)
    if (socket.connected) tryReconnect();

    // Also fire on future connect events (fresh page load or mid-game reconnect)
    socket.on('connect', tryReconnect);
    return () => socket.off('connect', tryReconnect);
  }, [user]); // eslint-disable-line

  // ── Socket listeners ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!user) return;

    socket.on('waiting_for_opponent', ({ gameId: gid }) => {
      setGameId(gid);
      setMatchState('searching');
    });

    socket.on('game_start', ({ gameId: gid, whitePlayer, blackPlayer }) => {
      const color = whitePlayer.id === user.userId ? 'white' : 'black';
      const opp   = color === 'white' ? blackPlayer : whitePlayer;
      localStorage.setItem(ACTIVE_GAME_KEY, JSON.stringify({ gameId: gid, timestamp: Date.now(), playerColor: color }));
      setGameId(gid);
      setPlayerColor(color);
      playerColorRef.current = color;
      setOpponent(opp);
      setReconnectMoves(null);
      setLastEloChange(null);
      setHasResumeGame(false);
      setResumeExpired(false);
      setOpponentDisconnected(false);
      setMatchState('in_game');
    });

    socket.on('game_reconnected', ({ gameId: gid, playerColor: color, whitePlayer, blackPlayer, moves }) => {
      const opp = color === 'white' ? blackPlayer : whitePlayer;
      localStorage.setItem(ACTIVE_GAME_KEY, JSON.stringify({ gameId: gid, timestamp: Date.now(), playerColor: color }));
      setGameId(gid);
      setPlayerColor(color);
      playerColorRef.current = color;
      setOpponent(opp);
      setReconnectMoves(moves || []);
      setLastEloChange(null);
      setHasResumeGame(false);
      setResumeExpired(false);
      setOpponentDisconnected(false);
      setMatchState('in_game');
    });

    socket.on('reconnect_failed', () => {
      localStorage.removeItem(ACTIVE_GAME_KEY);
      setHasResumeGame(false);
      setResumeExpired(true);
    });

    socket.on('opponent_disconnected', () => {
      setOpponentDisconnected(true);
    });

    socket.on('opponent_reconnected', () => {
      setOpponentDisconnected(false);
    });

    socket.on('game_ended', ({ eloChanges } = {}) => {
      localStorage.removeItem(ACTIVE_GAME_KEY);
      if (eloChanges && playerColorRef.current) {
        const mine = eloChanges[playerColorRef.current];
        if (mine) setLastEloChange(mine);
      }
      setMatchState(null);
      setGameId(null);
      setPlayerColor(null);
      playerColorRef.current = null;
      setOpponent(null);
      setReconnectMoves(null);
      setOpponentDisconnected(false);
      setHasResumeGame(false);
    });

    return () => {
      socket.off('waiting_for_opponent');
      socket.off('game_start');
      socket.off('game_reconnected');
      socket.off('reconnect_failed');
      socket.off('opponent_disconnected');
      socket.off('opponent_reconnected');
      socket.off('game_ended');
    };
  }, [user]);

  // ── Auth handlers ────────────────────────────────────────────────────────────
  async function handleRegister(e) {
    e.preventDefault();
    setMessage('');
    setAuthLoading(true);
    try {
      const data = await registerUser(email, username, password);
      if (data.error) { setMessage(data.error); return; }
      setMessage('Account created — please log in.');
      setAuthMode('login');
    } catch {
      setMessage('Something went wrong. Please try again.');
    } finally {
      setAuthLoading(false);
    }
  }

  async function handleLogin(e) {
    e.preventDefault();
    setMessage('');
    setAuthLoading(true);
    try {
      const data = await loginUser(loginUsername, password);
      if (data.error) { setMessage(data.error); return; }
      localStorage.setItem('checkmateUser', JSON.stringify(data));
      setUser(data);
      try {
        if (data.token && data.refreshToken) {
          await supabase.auth.setSession({ access_token: data.token, refresh_token: data.refreshToken });
        }
      } catch { /* session sync is best-effort */ }
    } catch {
      setMessage('Something went wrong. Please try again.');
    } finally {
      setAuthLoading(false);
    }
  }

  function handleLogout() {
    supabase.auth.signOut().catch(() => {});
    localStorage.removeItem('checkmateUser');
    localStorage.removeItem(ACTIVE_GAME_KEY);
    setUser(null);
    setMatchState(null);
    setGameId(null);
    setPlayerColor(null);
    setOpponent(null);
    setReconnectMoves(null);
    setOpponentDisconnected(false);
    setHasResumeGame(false);
    setResumeExpired(false);
  }

  function handleResumeMatch() {
    const stored = localStorage.getItem(ACTIVE_GAME_KEY);
    if (!stored) { setHasResumeGame(false); return; }
    try {
      const { gameId: storedId, timestamp } = JSON.parse(stored);
      // Optimistically check client-side window before emitting
      if (Date.now() - timestamp >= ABANDON_GRACE * 3) {
        // Way past window — clear without asking server
        localStorage.removeItem(ACTIVE_GAME_KEY);
        setHasResumeGame(false);
        setResumeExpired(true);
        return;
      }
      socket.emit('reconnect_game', { userId: user.userId, gameId: storedId });
    } catch {
      localStorage.removeItem(ACTIVE_GAME_KEY);
      setHasResumeGame(false);
    }
  }

  // ── Matchmaking ──────────────────────────────────────────────────────────────
  function handleFindGame() {
    if (!user) return;
    socket.emit('find_game', { userId: user.userId, username: user.username });
    setMatchState('searching');
  }

  function handleCancelSearch() {
    if (gameId) socket.emit('cancel_search', { gameId });
    setMatchState(null);
    setGameId(null);
  }

  // Must be before early returns — hooks can't be called conditionally
  const multiplayer = useMemo(
    () => matchState === 'in_game' ? { socket, gameId, playerColor, opponent, reconnectMoves } : null,
    [matchState, gameId, playerColor, opponent, reconnectMoves] // eslint-disable-line
  );

  // ── Session restoring ────────────────────────────────────────────────────────
  if (sessionLoading) {
    return (
      <div style={{
        minHeight: "100vh", background: DS.bg,
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <div style={{ fontSize: 36, color: DS.gold, animation: "fadeUp 0.6s ease both" }}>♔</div>
      </div>
    );
  }

  // ── Not logged in → auth screen ──────────────────────────────────────────────
  if (!user) {
    return (
      <div style={{
        minHeight: "100vh", background: DS.bg, color: DS.text,
        display: "flex", alignItems: "center", justifyContent: "center",
        backgroundImage: "repeating-conic-gradient(rgba(255,255,255,0.012) 0% 25%, transparent 0% 50%)",
        backgroundSize: "64px 64px",
      }}>
        <div style={{
          width: 360, background: DS.surface,
          border: `1px solid ${DS.borderFaint}`,
          borderRadius: DS.radius, padding: "36px 32px",
          boxShadow: "0 32px 80px rgba(0,0,0,0.5)",
        }}>
          {/* Logo */}
          <div style={{ textAlign: "center", marginBottom: 32 }}>
            <div style={{ fontSize: 48, color: DS.gold, marginBottom: 12, filter: `drop-shadow(0 0 20px ${DS.goldGlow})` }}>♔</div>
            <div className="cinzel-deco" style={{ fontSize: 22, color: DS.text, letterSpacing: "0.1em" }}>CHECKMATE</div>
            <div className="cinzel" style={{ fontSize: 10, color: DS.textMuted, letterSpacing: "0.35em", marginTop: 8, textTransform: "uppercase" }}>
              {authMode === 'login' ? 'Sign In to Your Account' : 'Create Account'}
            </div>
          </div>

          {/* Form */}
          <form onSubmit={authMode === 'login' ? handleLogin : handleRegister}
            style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {authMode === 'login' ? (
              <input
                type="text" placeholder="Username" value={loginUsername} required
                onChange={e => setLoginUsername(e.target.value)}
                style={inputStyle}
              />
            ) : (
              <>
                <input
                  type="email" placeholder="Email" value={email} required
                  onChange={e => setEmail(e.target.value)}
                  style={inputStyle}
                />
                <input
                  type="text" placeholder="Username" value={username} required
                  onChange={e => setUsername(e.target.value)}
                  style={inputStyle}
                />
              </>
            )}
            <input
              type="password" placeholder="Password" value={password} required
              onChange={e => setPassword(e.target.value)}
              style={inputStyle}
            />
            {message && (
              <div style={{ fontSize: 12, color: DS.danger, padding: "6px 10px", background: "rgba(224,70,70,0.1)", borderRadius: 6 }}>
                {message}
              </div>
            )}
            <button type="submit" disabled={authLoading} className="btn btn-primary cinzel" style={{ marginTop: 4, padding: "12px", fontSize: 12, letterSpacing: "0.08em", opacity: authLoading ? 0.65 : 1 }}>
              {authLoading
                ? (authMode === 'login' ? 'Signing in…' : 'Creating account…')
                : (authMode === 'login' ? 'Sign In' : 'Create Account')}
            </button>
          </form>

          {/* Toggle */}
          <div style={{ textAlign: "center", marginTop: 20 }}>
            <button
              onClick={() => { setAuthMode(authMode === 'login' ? 'register' : 'login'); setMessage(''); setAuthLoading(false); }}
              className="cinzel"
              style={{ background: "none", border: "none", cursor: "pointer", fontSize: 11, color: DS.textMuted, letterSpacing: "0.04em" }}
            >
              {authMode === 'login' ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Logged in ────────────────────────────────────────────────────────────────

  return (
    <div style={{ position: "relative" }}>
      <GameApp
        user={user}
        onPlayOnline={handleFindGame}
        multiplayer={multiplayer}
        onLogout={handleLogout}
        eloChange={lastEloChange}
        opponentDisconnected={opponentDisconnected}
        hasResumeGame={hasResumeGame}
        resumeExpired={resumeExpired}
        onResumeMatch={handleResumeMatch}
      />

      {/* ── Matchmaking overlay ── */}
      {matchState === 'searching' && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(13,12,24,0.88)",
          backdropFilter: "blur(6px)", display: "flex",
          alignItems: "center", justifyContent: "center", zIndex: 500,
        }}>
          <div style={{
            background: DS.surface, border: `1px solid ${DS.borderFaint}`,
            borderRadius: DS.radius, padding: "40px 48px", textAlign: "center",
            boxShadow: "0 32px 80px rgba(0,0,0,0.6)",
          }}>
            <div style={{ fontSize: 48, marginBottom: 20, animation: "fadeUp 1.5s ease infinite" }}>♔</div>
            <div className="cinzel" style={{ fontSize: 14, fontWeight: 700, color: DS.text, letterSpacing: "0.08em", marginBottom: 8 }}>
              Finding Opponent
            </div>
            <div style={{ fontSize: 12, color: DS.textMuted, marginBottom: 28 }}>
              Searching for a worthy challenger…
            </div>
            <button className="btn btn-ghost btn-sm cinzel" onClick={handleCancelSearch}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const inputStyle = {
  width: "100%", padding: "11px 14px", boxSizing: "border-box",
  background: DS.elevated, border: `1px solid ${DS.border}`,
  borderRadius: 8, color: DS.text, fontSize: 13,
  outline: "none", fontFamily: "inherit",
};
